import { Fragment } from 'react';
import { GovIcon } from '@gov-design-system-ce/react';
import { MarkdownRender } from '@gov-nx/component/web';
import { useDataBoxConnectBanner } from '@gov-nx/core/hooks';
import { pipe, pushWhen } from '@gov-nx/core/types';
import type { DataBoxConnectBannerProps } from '@gov-nx/ui/types';
import { Button } from '../Button/Button';
import { ButtonGroupLayout } from '../Layout/ButtonGroupLayout';

export const DataBoxConnectBanner = ({
	requiredTypes,
	translationPrefix,
}: DataBoxConnectBannerProps) => {
	const {
		events,
		strings,
		showCreateButton,
		showCreateFOButton,
		showConnectButton,
		possibleToConnectOrCreate,
	} = useDataBoxConnectBanner({
		requiredTypes,
		translationPrefix,
	});

	if (!possibleToConnectOrCreate) {
		return null;
	}

	const buttons = pipe(
		[],
		pushWhen(
			showConnectButton,
			<Button
				color="primary"
				type="solid"
				size="s"
				onClick={() => events.connect({ requiredTypes })}
				aria-label={strings.buttons.connect.wcag}>
				{strings.buttons.connect.content}
			</Button>
		),
		pushWhen(
			showCreateButton,
			<Button
				color="primary"
				type="solid"
				size="s"
				onClick={events.create}
				aria-label={strings.buttons.create.wcag}>
				{strings.buttons.create.content}
			</Button>
		),
		pushWhen(
			showCreateFOButton,
			<Button
				color="primary"
				type="solid"
				size="s"
				onClick={events.create}
				aria-label={strings.buttons.createFO.wcag}>
				{strings.buttons.createFO.content}
			</Button>
		)
	);

	return (
		<div
			role="status"
			className="p-(--templates-margin-m) bg-(--background-block-primary) rounded-3xl">
			<div className="flex flex-col gap-(--spacing-m-nudge) items-start p-(--spacing-m) [ md:flex-row ]">
				<div className="flex justify-center items-center w-16 h-16 bg-(--background-primary) rounded-full text-(--color-secondary-600)">
					<GovIcon
						type="templates"
						name="datovka-fill"
						size="3xl"
					/>
				</div>
				<div className="flex flex-col gap-(--spacing-s) flex-1">
					<h3 className="text-headline-xs!">{strings.title}</h3>
					<MarkdownRender
						applyClasses={{
							paragraph: 'text-body-s! text-(--text-secondary)',
						}}>
						{strings.content}
					</MarkdownRender>
					{buttons.length > 1 && (
						<ButtonGroupLayout>
							{buttons.map((button) => (
								<Fragment key={JSON.stringify(button.props)}>{button}</Fragment>
							))}
						</ButtonGroupLayout>
					)}
					{buttons.length === 1 && buttons[0]}
				</div>
			</div>
		</div>
	);
};
